'use strict';
/* ==========================================================================
   levels/map.js — the hub: Dr. Quinn's field kit on a kraft desk. Left: an old survey sheet (contour lines, the real
   2025 quake map clipped on) with Quinn standing on a torn-paper strata column + a speech bubble. Right: the field
   notebook page. Right page: one paper luggage tag per lesson.json step (so a new
   step shows up here by itself) → Game.replace(new Levels[id]()). A stamped step gets a rubber stamp (its
   score when Game.progress.scores[id] = {best, of}); the first step not done yet gets a red "next!" arrow.
   On a phone held upright the steps are full-width rows in the portrait dock (Dock.map) and the notebook is
   only the picture (no hit areas, no bubble).
   ========================================================================== */
(function () {
  const COLORS = [PAL.red, PAL.mustard, PAL.teal, PAL.navy, PAL.pink, PAL.lilac];
  const TAG_W = 680;
  // Dr. Quinn's line on the map (captions only, not voiced)
  const SAY = {
    hello: 'Start with tag one. Then we build mountains.',
    next: 'Gorgeous. Next stop: the tag with the arrow.',
    done: 'All stamped. You now know more than most rocks.',
  };

  /* the tags, laid out from lesson.json steps (numbers count the steps that earn a stamp, like the home page) */
  function tags() {
    const S = (Game.lesson && Game.lesson.steps) || [], n = Math.max(1, S.length);
    const gap = Math.min(250, 760 / n), h = Math.min(160, gap - 22);
    let k = 0;
    return S.map((s, i) => {
      const stamps = s.stamp !== false; if (stamps) k++;
      return { id: s.id, n: stamps ? k : null, title: s.title, sub: s.tag || '', h, x: 1380 + [0, 15, -10, 12, -6][i % 5],
        y: 250 + (i + 0.5) * (760 / n), rot: [-0.03, 0.025, -0.018, 0.02, -0.022][i % 5], color: stamps ? COLORS[(k - 1) % COLORS.length] : PAL.sky };
    });
  }
  const nextStep = () => tags().find(tg => tg.n != null && !Game.progress.stamps[tg.id]);

  function page(x, w, seed, margin) {
    paperRect(x, 545, w, 960, PAL.cream, { seed, depth: 1.6, boil: false });
    for (let y = 190; y < 1010; y += 58) ink([[x - w / 2 + 16, y], [x + w / 2 - 16, y]], { w: 2, color: 'rgba(90,150,190,0.45)', seed: seed * 50 + y, passes: 1, step: 80, amp: 0.8 });
    if (margin) ink([[margin, 80], [margin, 1015]], { w: 2.5, color: 'rgba(224,83,61,0.6)', seed: seed + 7, passes: 1, step: 80, amp: 0.8 });
  }

  function drawTag(tg, i, hover, t0) {
    const s = pop(T, t0 + 0.35 + i * 0.14, 0.4, 2.2);
    if (s <= 0) return;
    const H = tg.h;
    g.save();
    g.translate(tg.x, tg.y + (hover ? -8 : 0)); g.rotate(tg.rot + (hover ? -0.015 : 0)); g.scale(s * (hover ? 1.03 : 1), s * (hover ? 1.03 : 1));
    ink([[-TAG_W / 2 + 40, 0], [-TAG_W / 2 - 30, -40], [-TAG_W / 2 - 60, 10]], { w: 4, color: '#8a6a45', seed: 40 + i });
    paperRect(0, 0, TAG_W, H, PAL.white, { seed: 40 + i, depth: hover ? 2 : 1.2 });
    paperRect(-TAG_W / 2 + 12, 0, 24, H - 10, tg.color, { seed: 45 + i, depth: 0 });
    g.beginPath(); g.arc(-TAG_W / 2 + 40, 0, 10, 0, TAU); g.fillStyle = 'rgba(80,60,40,0.55)'; g.fill();
    paperCircle(-TAG_W / 2 + 140, 0, H * 0.36, tg.color, { seed: 50 + i, depth: 0.7 });
    g.font = `${Math.round(H * 0.52)}px 'Luckiest Guy'`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = PAL.cream;
    g.fillText(tg.n == null ? '▶' : String(tg.n), -TAG_W / 2 + 140, H * 0.04);
    // the title fits the tag (long titles shrink instead of running into the "next!" arrow)
    let ts = Math.round(H * 0.42);
    g.font = `${ts}px 'Caveat Brush'`; const tw = g.measureText(tg.title).width, room = TAG_W - 232 - 40;
    if (tw > room) ts = Math.floor(ts * room / tw);
    hand(tg.title, -TAG_W / 2 + 232, -H * 0.17, ts, t0 + 0.6 + i * 0.14, { align: 'left', seed: 55 + i });
    hand(tg.sub, -TAG_W / 2 + 236, H * 0.28, Math.min(Math.round(H * 0.3), UI.text(36)), t0 + 0.9 + i * 0.14, { align: 'left', color: PAL.navy, seed: 58 + i });
    g.restore();
  }

  class MapScene extends Scene {
    enter() {
      const st = Game.progress.stamps, T_ = tags().filter(tg => tg.n != null);
      this.allDone = T_.every(tg => st[tg.id]);
      this.say = this.allDone ? SAY.done : T_.some(tg => st[tg.id]) ? SAY.next : SAY.hello;
      this.stampNew = Game._justStamped; Game._justStamped = null;
      Audio.play('paper', { gain: 0.5 });
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
    }
    open(id) {
      Audio.play('pop', { gain: 0.6 });
      const C = Levels[id];
      if (typeof C === 'function') Game.replace(new C());
      else UI.toast('coming soon');
    }
    keyDown(k) {
      const all = tags(), n = parseInt(k, 10);
      if (n >= 1 && n <= all.length) this.open(all[n - 1].id);
      if (k === 'Enter') this.open((nextStep() || all[0]).id);
    }

    draw() {
      UI.allLessons();                       // "← All lessons": back to the site's home page (core/ui.js)
      const t0 = this.enteredAt, t = this.t, docked = UI.docked();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      // left: the survey sheet, the real quake map clipped on, Quinn on a strata column
      GroundLook.survey(548, 545, 830, 960, { seed: 81, depth: 1.6, torn: false });
      GroundLook.quakeMap(560, 250, 520, 224, { seed: 83, rot: 0.02, depth: 1, plates: true });
      tape(330, 150, 110, -0.6, undefined, 7);
      g.save(); g.beginPath(); g.rect(133, 65, 830, 960); g.clip();
      GroundLook.strata(905, 140, { seed: 9, x: 548, w: 900, tilt: -0.01 });
      g.restore();
      page(1372, 830, 82, null);
      const gut = g.createLinearGradient(930, 0, 990, 0);
      gut.addColorStop(0, 'rgba(60,40,20,0)'); gut.addColorStop(0.5, 'rgba(60,40,20,0.28)'); gut.addColorStop(1, 'rgba(60,40,20,0)');
      g.fillStyle = gut; g.fillRect(930, 65, 60, 960);
      for (let y = 110; y < 1010; y += 64) inkCircle(960, y, 15, { w: 4, sx: 1.6, color: '#6b6f78', seed: 90 + y, step: 6 });
      tape(170, 90, 160, -0.7, undefined, 5); tape(1760, 90, 160, 0.7, undefined, 6);
      ransom('FIELD KIT', 1372, 172, 56, t0 + 0.3, { seed: 304 });

      // Dr. Quinn + speech (the speech goes into the dock when docked)
      const rise = E.outBack(P(T, t0 + 0.2, t0 + 0.7), 1.4);
      Puppet.draw('quinn', 300, 930 + (1 - rise) * 500, 540, { facing: 1, t: T, env: t > 0.6 && t < 3 ? 0.25 + 0.25 * Math.sin(T * 17) : 0 });
      if (docked) Dock.map({ say: this.say, who: (LessonHooks.speakers && LessonHooks.speakers.quinn || {}).name, open: id => this.open(id) });
      else if (t > 0.6) UI.speech(this.say, 660, 560, { side: 'left', w: 440, t0: t0 + 0.6 });

      const nx = nextStep();
      tags().forEach((tg, i) => {
        const hit = docked ? { hover: false, clicked: false } : UI._hit('tag' + i, tg.x, tg.y, TAG_W, UI.tap(tg.h));
        if (hit.hover && !this['hov' + i]) Audio.play('paper', { gain: 0.2, rate: 1.3 });
        this['hov' + i] = hit.hover;
        drawTag(tg, i, hit.hover, t0);
        if (Game.progress.stamps[tg.id]) {
          let s = 1;
          if (this.stampNew === tg.id) {
            const at = t0 + 0.9;
            s = pop(T, at, 0.18, 1.2) * (1 + 1.4 * (1 - P(T, at, at + 0.12)));
            if (T >= at && !this.stampPlayed) { this.stampPlayed = true; Audio.play('stamp'); }
          } else s = pop(T, t0 + 0.8, 0.25, 1.5);
          const sc = Game.progress.scores && Game.progress.scores[tg.id];
          UI.stamp(sc && sc.best != null && sc.of ? `${sc.best}/${sc.of}` : 'DONE', tg.x + 250, tg.y + tg.h * 0.22, { rot: -0.16, s: s * 0.56 });
        }
        if (!this.allDone && nx && nx.id === tg.id) {
          const ns = pop(T, t0 + 1.4, 0.4), bob = Math.sin(T * 4) * 8;
          if (ns > 0) {
            g.save(); g.globalAlpha = ns;
            inkArrow(tg.x + TAG_W / 2 + 105 + bob, tg.y - 22, tg.x + TAG_W / 2 + 16 + bob, tg.y, { w: 6, color: PAL.red, seed: 600 + i, bulge: -20 });
            hand('next!', tg.x + TAG_W / 2 + 92 + bob, tg.y - 56, 42, 0, { dur: 0.01, color: PAL.red, rot: 0.1, seed: 610 + i });
            g.restore();
          }
        }
        if (hit.clicked) this.open(tg.id);
      });
    }
  }
  MapScene.tags = () => tags().map(tg => ({ id: tg.id, x: tg.x, y: tg.y }));   // tools/integration.js clicks these

  Levels.map = MapScene;
})();
